import CheckmarkIcon from '@assets/icons/checkmark.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { BaseActionSheet } from './base-action-sheet';
import { Button } from './button';
import { CheckBoxActionSheetOptions, CheckboxGroup } from './checkbox-group';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { Platform, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props<T> {
  buttonRef?: React.RefObject<View | null>;
  defaultSelectedOptions?: Set<string>;
  defaultSettingsActive?: boolean;
  headerElement?: React.ReactNode;
  initialSelectedOptions?: Set<string>;
  onApplyChanges: (selectedOptions: Set<string>) => void;
  onApplyDefaultChanges?: (selectedOptions: Set<string>) => void;
  onClose: () => void;
  options: CheckBoxActionSheetOptions<T>[];
  setDefaultSettingsActive?: (active: boolean) => void;
  title: string;
}

export const MultipleOptionsActionSheet = <T extends string = string>({
  headerElement,
  initialSelectedOptions,
  onApplyChanges,
  onClose,
  options,
  title,
  defaultSettingsActive = false,
  setDefaultSettingsActive,
  defaultSelectedOptions,
  onApplyDefaultChanges,
  buttonRef,
}: Props<T>) => {
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();
  const [styles, theme] = useStyles();
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    initialSelectedOptions ?? new Set(),
  );
  const defaultSetterEnabled = Boolean(setDefaultSettingsActive);

  const renderAsPopover = Boolean(!isSmallLayout && buttonRef);

  useEffect(() => {
    if (defaultSettingsActive) {
      // if moving into Default, predefined those in default + selected ones
      const newSelectedOpts = Array.from(selectedOptions).concat(
        Array.from(defaultSelectedOptions ?? new Set<string>()),
      );
      setSelectedOptions(new Set(newSelectedOpts));
    } else {
      setSelectedOptions(initialSelectedOptions ?? new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSettingsActive]);

  const disableDefaultSetterBtn =
    selectedOptions.size === 0 && !defaultSettingsActive;

  return (
    <BaseActionSheet
      contentStyle={[
        styles.baseContent,
        renderAsPopover ? styles.popoverContent : null,
      ]}
      isVisible
      onClose={onClose}
      popoverTarget={buttonRef?.current}
      style={[styles.base, renderAsPopover ? null : styles.modalStyle]}
    >
      {headerElement ? headerElement : null}
      {defaultSetterEnabled ? (
        <View style={styles.defaultSetterView}>
          <Button
            disabled={disableDefaultSetterBtn}
            onPress={() => setDefaultSettingsActive?.(!defaultSettingsActive)}
            rightElement={
              defaultSettingsActive ? (
                <View style={styles.checkbox}>
                  <CheckmarkIcon
                    height={18}
                    stroke={
                      disableDefaultSetterBtn
                        ? theme.sk.color.icon.secondary
                        : theme.colors.primary
                    }
                    width={18}
                  />
                </View>
              ) : null
            }
            size="xsmall"
            trackingData={null}
            type="tertiary"
          >
            {defaultSettingsActive
              ? i18n.t('default')
              : `${i18n.t('set_default')}`}
          </Button>
        </View>
      ) : null}
      <CheckboxGroup
        onSelect={(option) => {
          setSelectedOptions((prev) => {
            const newSelectedOptions = prev.has(option)
              ? new Set(
                  [...prev].filter(
                    (filteredOption) => filteredOption !== option,
                  ),
                )
              : new Set([...prev, option]);
            return newSelectedOptions;
          });
        }}
        options={options}
        selectedOptions={selectedOptions}
        title={title}
        titleStyle={renderAsPopover ? styles.popoverTitle : styles.title}
      />
      <View
        style={
          renderAsPopover ? styles.popoverButtonsWrapper : styles.buttonsWrapper
        }
      >
        <Button
          containerStyle={renderAsPopover ? undefined : styles.buttonContainer}
          disabled={selectedOptions.size === 0}
          onPress={() => setSelectedOptions(new Set())}
          size={renderAsPopover ? 'xsmall' : undefined}
          style={renderAsPopover ? styles.popoverClearFilterButton : undefined}
          trackingData={null}
          type="tertiary"
        >
          {i18n.t('clear_filters')}
        </Button>
        <Button
          accessibilityLabel={i18n.t('apply')}
          containerStyle={
            renderAsPopover
              ? { ...styles.popoverButtonContainer, paddingBottom: bottom }
              : styles.buttonContainer
          }
          onPress={() => {
            if (
              defaultSetterEnabled &&
              defaultSettingsActive &&
              onApplyDefaultChanges
            ) {
              onApplyDefaultChanges(selectedOptions);
            } else {
              onApplyChanges(selectedOptions);
            }
            onClose();
          }}
          size={renderAsPopover ? 'xsmall' : undefined}
          style={renderAsPopover ? styles.popoverApplyFilterButton : undefined}
          trackingData={null}
        >
          {i18n.t('apply')}
        </Button>
      </View>
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    marginHorizontal: 0,
  },
  baseContent: {
    flex: Platform.OS === 'web' ? 1 : undefined,
    paddingHorizontal: 0,
    paddingTop: 16,
  },
  buttonContainer: {
    flex: 1,
  },
  buttonsWrapper: {
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-evenly',
    paddingHorizontal: 24,
  },
  checkbox: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    height: 22,
    justifyContent: 'center',
    width: 22,
  },
  defaultSetterView: {
    position: 'absolute',
    right: 10,
    top: 32,
    zIndex: 2,
  },
  modalStyle: {
    marginTop: 190,
  },
  popoverApplyFilterButton: {
    height: 32,
    paddingHorizontal: 20,
  },
  popoverButtonContainer: {
    justifyContent: 'center',
    width: 60,
  },
  popoverButtonsWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  popoverClearFilterButton: {
    height: 32,
    paddingHorizontal: 0,
  },
  popoverContent: {
    maxHeight: 400,
    width: 300,
  },
  popoverTitle: {
    ...TYPOGRAPHY.medium,
    ...FONTS.primarySemiBold,
    paddingBottom: 16,
    paddingLeft: 24,
  },
  title: {
    ...TYPOGRAPHY.h2,
    ...FONTS.primarySemiBold,
    paddingBottom: 16,
    paddingLeft: 24,
  },
}));
